import { useCallback, useEffect, useState } from "react";

// This hook is created to rotate the title with a set interval
export const useFlashingTitle = (
    primaryPageTitle: string,
    secondaryPageTitle?: string
) => {
    const [currentTitle, setCurrentTitle] = useState<string>(primaryPageTitle);
    const [eventId, setEvent] = useState<number | null>(null);

    const startFlasher = useCallback((): number | null => {
        if (!secondaryPageTitle) {
            return null;
        }
        return window.setInterval(() => {
            // use a second form of setState() that accepts a function rather than an object. That function will receive the previous state as the first argument
            // https://reactjs.org/docs/state-and-lifecycle.html#state-updates-may-be-asynchronous
            setCurrentTitle((visibleTitle) =>
                visibleTitle === secondaryPageTitle
                    ? primaryPageTitle
                    : secondaryPageTitle
            );
        }, 1000);
    }, [primaryPageTitle, secondaryPageTitle]);

    const clearEvent = useCallback((): void => {
        if (eventId) {
            window.clearInterval(eventId);
            setEvent(null);
        }
    }, [eventId]);

    const listener = useCallback(() => {
        if (document.hidden) {
            // The page is hidden, start the flasher to bring their attention back to our page
            setEvent(startFlasher());
        } else {
            // The page is back in view now, set the primary title and stop flashing
            clearEvent();
            setCurrentTitle(primaryPageTitle);
        }
    }, [clearEvent, startFlasher, setCurrentTitle, primaryPageTitle]);

    useEffect(() => {
        document.addEventListener("visibilitychange", listener, { passive: true });
        return () => {
            document.removeEventListener("visibilitychange", listener);
            clearEvent();
        };
    }, [listener, clearEvent]);

    return currentTitle;
};
